import { useCallback, useEffect, useMemo } from 'react';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import type { PageUI } from '@alltrails/layout/utils/PageUI/pageUI';
import usePageUITrailResults from '@alltrails/maps/hooks/usePageUITrailResults';
import TrailResultsMapComponents from '@/components/TrailResultsMapComponents/TrailResultsMapComponents';
import { askAllTrailsApi, type AskAllTrailsArgs } from '@/utils/askAllTrailsApi';
import { type RootState, useAppDispatch, useAppSelector } from '@/utils/redux';
import { selectAskTurnsThroughActive } from '@/utils/slices/askSlice';

const hasTrailResults = (page: PageUI | undefined) => !!page?.sections.some(section => section.sectionType === 'trail-result');

const turnSelectors = new WeakMap<AskAllTrailsArgs, ReturnType<typeof askAllTrailsApi.endpoints.askAllTrails.select>>();

const selectTurnPage = (state: RootState, args: AskAllTrailsArgs) => {
  let selector = turnSelectors.get(args);

  if (!selector) {
    selector = askAllTrailsApi.endpoints.askAllTrails.select(args);
    turnSelectors.set(args, selector);
  }

  return selector(state).data;
};

/**
 * The answer the user is reading, or if it has no trails to pin, the closest one before it that does. A chat follow-up
 * answered in text alone (ex: "is the first one good in the rain?") keeps the earlier answer's pins on the map. With none,
 * the answer itself, so a search that found nothing still clears them.
 */
const selectMapPage = (state: RootState) => {
  const pages = selectAskTurnsThroughActive(state).map(args => selectTurnPage(state, args));

  return pages.findLast(hasTrailResults) ?? pages.at(-1);
};

/**
 * What the map draws while Ask AllTrails is showing, in place of the page's own map components.
 */
const AskMapComponents = () => {
  const dispatch = useAppDispatch();
  const turns = useAppSelector(state => state.ask.turns);
  const page = useAppSelector(selectMapPage);

  // Holds every turn's answer while the map can fall back to it, whether or not the sidebar is showing it
  useEffect(() => {
    const subscriptions = turns.map(args => dispatch(askAllTrailsApi.endpoints.askAllTrails.initiate(args)));

    return () => subscriptions.forEach(subscription => subscription.unsubscribe());
  }, [dispatch, turns]);

  usePageUITrailResults(page);

  // From the server's analytics on each trail result, like the sidebar's cards
  const trailIdsWithInsights = useMemo(
    () =>
      new Set(
        page?.sections.flatMap(section => (section.sectionType === 'trail-result' && section.eventProperties.hasInsight ? [section.trailId] : []))
      ),
    [page]
  );
  const hasCommunityInsights = useCallback((trailId: number) => trailIdsWithInsights.has(trailId), [trailIdsWithInsights]);

  return (
    <TrailResultsMapComponents
      cardLocation="ask alltrails"
      detailedCardLocation="ask alltrails results"
      hasCommunityInsights={hasCommunityInsights}
      mapLocation={PageStrings.ASK_ALLTRAILS_PAGE}
    />
  );
};

export default AskMapComponents;
